import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LucideIcon, X } from 'lucide-react';

interface MetricDetailModalProps {
  open: boolean;
  onClose: () => void;
  icon: LucideIcon;
  title: string;
  value: string;
  subtitle: string;
  sparkData: number[];
  anchorRect?: DOMRect | null;
  metricKey: string;
}

const HOUR_LABELS = ['0h','2h','4h','6h','8h','10h','12h','14h','16h','18h','20h','22h'];

const METRIC_CONFIG: Record<string, {
  comparison: string;
  comparisonColor: string;
  comparisonIcon: string;
  unit: string;
  insights: { emoji: string; text: string }[];
}> = {
  plays: {
    comparison: 'hoje',
    comparisonColor: '#2563EB',
    comparisonIcon: '▲',
    unit: 'plays',
    insights: [
      { emoji: '•', get text() { return translateInterfaceText("Distribuição horária dos plays do dia"); } },
      { emoji: '•', get text() { return translateInterfaceText("Pico no horário de maior audiência"); } },
    ],
  },
  playRate: {
    comparison: 'hoje',
    comparisonColor: '#2563EB',
    comparisonIcon: '',
    unit: '%',
    insights: [
      { emoji: '•', get text() { return translateInterfaceText("Plays divididos pelas visualizações únicas do dia"); } },
      { emoji: '•', get text() { return translateInterfaceText("A curva mostra o play rate de cada hora"); } },
    ],
  },
  views: {
    comparison: 'hoje',
    comparisonColor: '#2563EB',
    comparisonIcon: '▲',
    unit: 'visualizações',
    insights: [
      { emoji: '•', get text() { return translateInterfaceText("Total de visualizações únicas do dia"); } },
      { emoji: '•', get text() { return translateInterfaceText("Acesse Métricas para ver detalhes por vídeo"); } },
    ],
  },
  retention: {
    get comparison() { return translateInterfaceText("média hoje"); },
    comparisonColor: '#2563EB',
    comparisonIcon: '',
    unit: '%',
    insights: [
      { emoji: '•', get text() { return translateInterfaceText("Percentual médio assistido dos vídeos"); } },
      { emoji: '•', get text() { return translateInterfaceText("Acesse Teste de hook para otimizar retenção"); } },
    ],
  },
  peak: {
    get comparison() { return translateInterfaceText("maior audiência"); },
    comparisonColor: '#2563EB',
    comparisonIcon: '',
    unit: 'espectadores',
    insights: [
      { emoji: '•', get text() { return translateInterfaceText("Horário com maior volume de espectadores"); } },
      { emoji: '•', get text() { return translateInterfaceText("Use esse dado para agendar seus conteúdos"); } },
    ],
  },
  conversions: {
    comparison: 'hoje',
    comparisonColor: '#2563EB',
    comparisonIcon: '▲',
    unit: 'conversões',
    insights: [
      { emoji: '•', get text() { return translateInterfaceText("Total de conversões registradas hoje"); } },
      { emoji: '•', get text() { return translateInterfaceText("Acesse Métricas para ver por vídeo"); } },
    ],
  },
};

function InteractiveChart({ data, width, height, unit }: { data: number[]; width: number; height: number; unit: string }) {
  useLanguage();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const pad = 4;
  const chartW = width - pad * 2;
  const chartH = height - pad * 2;

  const { points, linePath, areaPath } = useMemo(() => {
    if (data.length < 2) return { points: [], linePath: '', areaPath: '' };
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    const pts = data.map((v, i) => ({
      x: pad + (i / (data.length - 1)) * chartW,
      y: pad + chartH - ((v - min) / range) * chartH,
      value: v,
    }));

    let line = `M${pts[0].x},${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(i - 1, 0)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(i + 2, pts.length - 1)];
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      line += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
    }
    const area = `${line} L${pts[pts.length - 1].x},${height} L${pts[0].x},${height} Z`;

    return { points: pts, linePath: line, areaPath: area };
  }, [data, width, height, chartW, chartH]);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || data.length < 2) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, (x - pad) / chartW));
    const idx = Math.round(ratio * (data.length - 1));
    setHoverIndex(idx);
  }, [data.length, chartW]);

  if (data.length < 2) return null;

  const hoveredPoint = hoverIndex !== null ? points[hoverIndex] : null;
  const hoveredHour = hoverIndex !== null ? `${(hoverIndex * Math.floor(24 / data.length))}h` : '';
  const hoveredValue = hoverIndex !== null ? data[hoverIndex] : 0;
  const prevValue = hoverIndex !== null && hoverIndex > 0 ? data[hoverIndex - 1] : null;
  const changePercent = prevValue !== null && prevValue > 0
    ? Math.round(((hoveredValue - prevValue) / prevValue) * 100)
    : null;

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="overflow-visible cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIndex(null)}
      >
        <defs>
          <linearGradient id="detail-chart-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2563EB" stopOpacity={0.08} />
            <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#detail-chart-grad)" />
        <path d={linePath} fill="none" stroke="#2563EB" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        {hoveredPoint && (
          <>
            <line x1={hoveredPoint.x} y1={pad} x2={hoveredPoint.x} y2={height} stroke="#2563EB" strokeWidth={1} strokeDasharray="3 3" opacity={0.4} />
            <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r={4} fill="#2563EB" stroke="#fff" strokeWidth={2} />
          </>
        )}
      </svg>
      {hoveredPoint && hoverIndex !== null && (
        <div
          className="absolute pointer-events-none bg-[#111827] text-white rounded-lg px-2.5 py-1.5 shadow-lg"
          style={{
            left: Math.min(Math.max(hoveredPoint.x - 50, 0), width - 110),
            top: -52,
            fontSize: 11,
            minWidth: 100,
          }}
        >
          <div className="font-semibold">{hoveredHour}</div>
          <div className="flex items-center gap-1.5">
            <span>{hoveredValue.toLocaleString(getInterfaceLanguage())} {unit}</span>
            {changePercent !== null && (
              <span style={{ color: changePercent >= 0 ? '#4ADE80' : '#F87171', fontSize: 10 }}>
                {changePercent >= 0 ? '▲' : '▼'} {changePercent >= 0 ? '+' : ''}{changePercent}%
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function MetricDetailModal({ open, onClose, icon: Icon, title, value, subtitle, sparkData, anchorRect, metricKey }: MetricDetailModalProps) {
  useLanguage();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [showContent, setShowContent] = useState(false);
  const config = METRIC_CONFIG[metricKey] || METRIC_CONFIG.plays;

  useEffect(() => {
    if (open) {
      setShowContent(false);
      const t = setTimeout(() => setShowContent(true), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) onClose();
    };
    const id = setTimeout(() => document.addEventListener('mousedown', handler), 10);
    return () => { clearTimeout(id); document.removeEventListener('mousedown', handler); };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const style: React.CSSProperties = {};
  if (anchorRect) {
    const popoverWidth = 340;
    let left = anchorRect.left + anchorRect.width / 2 - popoverWidth / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - popoverWidth - 12));
    style.position = 'fixed';
    style.top = anchorRect.bottom + 8;
    style.left = left;
    style.width = popoverWidth;
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/10"
          />
          <motion.div
            ref={popoverRef}
            initial={{ opacity: 0, y: -4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.96 }}
            transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
            className="fixed z-50 bg-popover text-popover-foreground rounded-xl shadow-lg border border-border overflow-hidden"
            style={style}
          >
            <div className="flex items-center justify-between px-4 pt-4 pb-1">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10">
                  <Icon className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
                </div>
                <h3 className="text-[13px] font-semibold text-foreground">{title}</h3>
              </div>
              <button
                onClick={onClose}
                className="w-6 h-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="px-4 pb-3 flex items-baseline gap-2.5">
              <span className="text-[26px] font-bold text-foreground tracking-[-0.02em] leading-none">{value}</span>
              {config.comparison && (
                <span className="text-[12px] font-medium flex items-center gap-1" style={{ color: config.comparisonColor }}>
                  {config.comparisonIcon} {config.comparison}
                </span>
              )}
            </div>

            <div className="px-4 pb-2">
              <div className="relative bg-muted/40 rounded-lg border border-border p-3">
                {!showContent ? (
                  <div className="w-full rounded bg-muted animate-pulse" style={{ height: 120 }} />
                ) : (
                  <>
                    <InteractiveChart data={sparkData} width={292} height={120} unit={config.unit} />
                    <div className="flex justify-between mt-1.5 px-0.5">
                      {HOUR_LABELS.slice(0, Math.min(sparkData.length, HOUR_LABELS.length)).map((label) => (
                        <span key={label} className="text-[9px] text-muted-foreground">{label}</span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="px-4 pb-4 pt-1 space-y-1.5">
              {config.insights.map((insight, i) => (
                <div key={i} className="flex items-start gap-2 text-[12px] text-foreground/80 leading-[1.4]">
                  <span className="flex-shrink-0 mt-px">{insight.emoji}</span>
                  <span>{insight.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
